/*
 * Lazy autoplay videos rendered by snippets/section-lazy-video.liquid.
 * - Loads the poster and MP4 about one screen before the video is reached.
 * - Videos marked data-section-video-warm-up (opt-in per section) start loading two screens ahead, show their poster
 *   as soon as the page is ready and also load in the background once the page has finished loading (skipped with
 *   Data Saver or a 2G connection), so they are usually buffered when reached. Only this device's copy is used.
 * - Plays while any pixel is on screen and pauses when fully off screen (it used to keep playing forever).
 * - A video inside a display:none container (the other device's copy) never intersects, so it never loads.
 * The snippet may include this script more than once; each video is set up only once.
 */
(() => {
  const isRendered = (video) => video.getClientRects().length > 0;
  const connection = navigator.connection;
  const canWarmUp = !connection?.saveData && !['slow-2g', '2g'].includes(connection?.effectiveType);
  const loaders = [];

  const setUp = (video) => {
    if (video.hasAttribute('data-section-video-ready')) return;
    video.setAttribute('data-section-video-ready', '');

    const sources = [...video.querySelectorAll('source[data-src]')];
    if (!sources.length) return;

    let isVisible = false;
    let isLoaded = false;

    const play = () => {
      if (isLoaded && video.paused) video.play()?.catch(() => {});
    };

    const showPoster = () => {
      if (video.dataset.poster && !video.getAttribute('poster')) video.poster = video.dataset.poster;
    };

    const load = () => {
      if (isLoaded) return;
      isLoaded = true;
      showPoster();
      sources.forEach((source) => {
        source.src = source.dataset.src;
      });
      video.preload = 'auto';
      video.load();
      if (isVisible) play();
    };

    if (video.hasAttribute('data-section-video-warm-up')) {
      if (isRendered(video)) showPoster();
      loaders.push({ video, load });
    }

    if (!('IntersectionObserver' in window)) {
      isVisible = true;
      load();
      return;
    }

    const loadObserver = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      load();
      loadObserver.disconnect();
    }, { rootMargin: video.hasAttribute('data-section-video-warm-up') ? '200% 0px' : '100% 0px' });

    const playObserver = new IntersectionObserver((entries) => {
      isVisible = entries.some((entry) => entry.isIntersecting);
      if (isVisible) {
        play();
      } else if (!video.paused) {
        video.pause();
      }
    }, { threshold: 0 });

    loadObserver.observe(video);
    playObserver.observe(video);
    video.addEventListener('play', () => {
      if (!isVisible) video.pause();
    });
  };

  const init = (root = document) => {
    root.querySelectorAll('video[data-section-video]').forEach(setUp);
  };

  const warmUp = () => {
    for (const { video, load } of loaders) {
      if (isRendered(video)) load();
    }
  };

  if (canWarmUp) {
    const scheduleWarmUp = () => {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(warmUp, { timeout: 2000 });
      } else {
        setTimeout(warmUp, 1000);
      }
    };
    if (document.readyState === 'complete') {
      scheduleWarmUp();
    } else {
      window.addEventListener('load', scheduleWarmUp, { once: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
